import React from "react";

const EqualityPolicy = () => {
  return (
    <div>
      <h1>Equality Policy</h1>
      <p>Last Updated: 11th August 2026</p>

      <p>
        This Equality Policy sets out the commitment of Paul Frederick David Walsh
        ("Owner", "Operator", "we", "us" or "our") to equality, dignity,
        fairness and respectful participation across the Pogforts services. The
        Services include pogforts.uk, the Pogforts web application and Discord
        server, the Magical Sorting Bat Discord bot ("MSB"), Pogforts: Murder
        Mystery Magic, community activities, game testing, moderation, authorised
        contributor roles and related services that we operate from time to time.
      </p>

      <p>
        This policy is intended to support compliance with applicable equality law,
        including the Equality Act 2010 where it applies to the provision of
        services. It also establishes standards of conduct that may go beyond the
        minimum legal requirements.
      </p>

      <h2>1. Scope</h2>
      <p>This policy applies, where relevant, to:</p>
      <ul>
        <li>members and visitors using Pogforts services;</li>
        <li>users of MSB and other Pogforts bot features;</li>
        <li>players and participants in Pogforts game systems;</li>
        <li>moderators, administrators and authorised contributors;</li>
        <li>game testers and volunteers;</li>
        <li>advertisers, collaborators, partners and service providers;</li>
        <li>
          applicants or candidates for community, moderation, testing or other
          roles controlled by the Operator.
        </li>
      </ul>

      <h2>2. Our Equality Commitment</h2>
      <p>We are committed to:</p>
      <ul>
        <li>providing Services fairly and without unlawful discrimination;</li>
        <li>
          treating users and participants with dignity and respect while recognising
          that lawful differences in treatment may sometimes be necessary to meet
          different needs or legal requirements;
        </li>
        <li>
          preventing and addressing unlawful discrimination, harassment and
          victimisation within areas we control;
        </li>
        <li>
          considering reasonable adjustments for disabled users where the law
          requires them and where relevant to the way a Service is provided;
        </li>
        <li>
          applying moderation and enforcement standards consistently while allowing
          proportionate decisions based on the facts of each case;
        </li>
        <li>
          fostering an environment in which people can participate without being
          targeted because of a protected characteristic.
        </li>
      </ul>

      <h2>3. Protected Characteristics</h2>
      <p>
        The Equality Act 2010 identifies nine protected characteristics:
      </p>
      <ul>
        <li>age;</li>
        <li>disability;</li>
        <li>gender reassignment;</li>
        <li>marriage and civil partnership;</li>
        <li>pregnancy and maternity;</li>
        <li>race;</li>
        <li>religion or belief;</li>
        <li>sex;</li>
        <li>sexual orientation.</li>
      </ul>
      <p>
        The exact statutory protection attached to a characteristic can vary by
        legal context. For example, marriage and civil partnership does not carry
        the same Part 3 service-provider protection as the other characteristics.
        As a matter of Pogforts policy, however, we aim not to treat people unfairly
        because of any of the characteristics listed above unless a difference in
        treatment is lawful, objectively justified or otherwise permitted by law.
      </p>

      <h2>4. Forms of Unacceptable Conduct</h2>
      <p>
        Conduct may breach this policy where it amounts to, encourages or creates a
        serious risk of:
      </p>
      <ul>
        <li>direct discrimination;</li>
        <li>indirect discrimination;</li>
        <li>harassment;</li>
        <li>sexual harassment;</li>
        <li>victimisation;</li>
        <li>
          failure to make a reasonable adjustment where a legal duty to do so
          applies;
        </li>
        <li>
          targeted abuse, exclusion or intimidation based on a protected
          characteristic;
        </li>
        <li>
          retaliation against a person for raising a genuine equality concern,
          supporting a complaint or participating in an investigation.
        </li>
      </ul>

      <h2>5. Application to the Pogforts Discord Community</h2>
      <ul>
        <li>
          Moderation decisions should be based on behaviour, evidence, applicable
          rules, safety and legitimate operational considerations rather than bias
          against a protected group.
        </li>
        <li>
          Reports of discriminatory harassment or targeted abuse should be assessed
          fairly and without prejudging the complainant or reported person.
        </li>
        <li>
          Sanctions may include warnings, restrictions, temporary removal,
          permanent removal or escalation where proportionate to the conduct and
          permitted by law and platform rules.
        </li>
        <li>
          Nothing in this policy prevents proportionate moderation of unlawful,
          harmful, threatening, abusive or disruptive behaviour merely because the
          person engaging in it has a protected characteristic.
        </li>
      </ul>

      <h2>6. Application to Games and Digital Features</h2>
      <ul>
        <li>
          Game mechanics should not intentionally assign advantages, disadvantages,
          sanctions or access restrictions because of a protected characteristic
          unless a lawful and legitimate exception applies.
        </li>
        <li>
          Fictional roles, rivalry, combat, competition, random outcomes and
          in-game sanctions are part of gameplay and are not, by themselves,
          discriminatory where they are applied by game rules unrelated to a
          protected characteristic.
        </li>
        <li>
          Accessibility concerns affecting digital features will be considered in
          good faith, including reasonable adjustments where a legal duty applies
          and a practicable adjustment is available.
        </li>
      </ul>

      <h2>7. Selection, Moderation and Contributor Roles</h2>
      <p>
        Where we select moderators, testers, volunteers, authorised contributors or
        other community-role holders, decisions should be based on legitimate
        factors such as capability, reliability, conduct, experience, trust,
        availability, security requirements and suitability for the role.
      </p>
      <p>
        Protected characteristics must not be used as a negative selection factor
        where doing so would be unlawful. Lawful positive action or other lawful
        exceptions may be considered where applicable.
      </p>

      <h2>8. Disability and Reasonable Adjustments</h2>
      <p>
        Where the Equality Act 2010 imposes a reasonable-adjustment duty in relation
        to a Service, we will consider reasonable steps to avoid substantial
        disadvantage to disabled people. What is reasonable depends on the
        circumstances, including effectiveness, practicality, resources, security,
        technical constraints and the nature of the Service.
      </p>
      <p>
        Users who require an adjustment should contact us with enough information
        to understand the barrier and the adjustment requested. We may discuss an
        alternative where the requested adjustment is not reasonably practicable or
        would materially compromise security or the Service.
      </p>

      <h2>9. Responsibilities of Users</h2>
      <ul>
        <li>Treat other people with dignity and respect.</li>
        <li>
          Do not engage in discriminatory harassment, targeted abuse or
          victimisation.
        </li>
        <li>
          Do not misuse this policy to make knowingly false allegations or to
          harass another person.
        </li>
        <li>
          Report serious equality concerns through the available moderation or
          contact channels.
        </li>
        <li>
          Cooperate reasonably with investigations where you choose to participate.
        </li>
      </ul>

      <h2>10. Responsibilities of Moderators and Administrators</h2>
      <ul>
        <li>Apply community rules consistently and impartially.</li>
        <li>Consider relevant evidence and context before taking action.</li>
        <li>
          Avoid stereotyping, prejudicial assumptions and irrelevant protected
          characteristics when making moderation decisions.
        </li>
        <li>
          Escalate serious threats, safeguarding issues or potential unlawful
          conduct where appropriate.
        </li>
        <li>
          Handle equality reports with appropriate discretion and respect for
          privacy.
        </li>
      </ul>

      <h2>11. Responsibilities of the Owner and Operator</h2>
      <p>Paul Frederick David Walsh, as Owner and Operator, will aim to:</p>
      <ul>
        <li>maintain this policy and review it when reasonably necessary;</li>
        <li>
          ensure moderation and operational rules are capable of being applied
          consistently;
        </li>
        <li>
          consider equality and accessibility issues when material Service changes
          are made;
        </li>
        <li>
          investigate credible reports falling within the scope of this policy;
        </li>
        <li>
          take proportionate corrective or disciplinary action where a breach is
          established;
        </li>
        <li>
          comply with applicable equality duties imposed on the provision of the
          Services.
        </li>
      </ul>

      <h2>12. Reporting an Equality Concern</h2>
      <p>
        If you experience or witness conduct that may breach this policy, you can
        report it through the designated Pogforts Discord moderation channels or
        another contact route we make available. A useful report should, where
        possible, identify what happened, when it happened, the people or accounts
        involved and any relevant evidence.
      </p>
      <p>
        Reports will be handled as discreetly as reasonably possible, but absolute
        confidentiality cannot be guaranteed where information must be shared to
        investigate, protect users, obtain advice or comply with law.
      </p>

      <h2>13. Investigation and Enforcement</h2>
      <ul>
        <li>
          We may gather relevant messages, logs, account information, witness
          accounts or other evidence available to us.
        </li>
        <li>
          The reported person may be given an opportunity to respond where
          appropriate and where doing so does not create an unreasonable safety or
          security risk.
        </li>
        <li>
          Decisions will be made on the information reasonably available at the
          time and may be revisited if material new evidence emerges.
        </li>
        <li>
          Action may range from guidance or warnings to restrictions, removal from
          roles, suspension, banning or referral to a platform provider or relevant
          authority where justified.
        </li>
      </ul>

      <h2>14. Privacy and Record Handling</h2>
      <p>
        Personal information used to receive, investigate or record equality
        concerns will be handled in accordance with the Pogforts Privacy Policy and
        applicable data protection law. Information will only be shared where there
        is a legitimate and lawful reason to do so.
      </p>

      <h2>15. External Platforms and Third Parties</h2>
      <p>
        Pogforts relies on third-party platforms, including Discord. We cannot
        control the independent decisions or policies of those platforms, but we may
        report conduct to them or cooperate with them where appropriate. Users may
        also have separate rights or complaint routes under a platform's own terms
        and policies.
      </p>

      <h2>16. No Retaliation for Genuine Complaints</h2>
      <p>
        A person must not be targeted, intimidated or disadvantaged merely because
        they made a genuine equality complaint, supported another person's complaint
        or participated in an investigation. This does not prevent action against
        knowingly false, malicious or abusive conduct where the evidence justifies
        it.
      </p>

      <h2>17. Changes to This Equality Policy</h2>
      <p>
        We may update this policy to reflect changes in law, guidance, Service
        design, moderation practice or community needs. The current version will be
        published on pogforts.uk.
      </p>

      <h2>18. Governing Framework</h2>
      <p>
        This policy is intended to operate consistently with the Equality Act 2010
        and other applicable law in England and Wales. It does not remove or reduce
        any statutory right or remedy available to a person.
      </p>

      <h2>19. Contact Information</h2>
      <p>
        Questions, accessibility requests or equality concerns can be raised through
        the Pogforts Discord server: {" "}
        <a
          href="https://discord.gg/8WF9QQJsVz"
          target="_blank"
          rel="noopener noreferrer"
        >
          https://discord.gg/8WF9QQJsVz
        </a>
      </p>

      <h2>20. Closing Statement</h2>
      <p>
        Pogforts is intended to be competitive, expressive and community-driven
        without permitting unlawful discrimination or targeted abuse. We expect
        users, moderators and contributors to uphold that standard while recognising
        that fairness does not always require identical treatment in every
        circumstance.
      </p>
    </div>
  );
};

export default EqualityPolicy;
